/**
 * Interface unifiée : mode texte + mode vocal.
 * - Zone agrandie, UI/UX senior
 * - Icône vocal après Envoyer dans la barre de saisie
 * - Mode vocal : gauche = messages/code, droite = avatar
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, Close, Send, Menu } from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import MessageBubble from './MessageBubble';
import CodeBlockWithCopy from './CodeBlockWithCopy';
import { useAssistantThreads } from '../../hooks/useAssistantThreads';
import { useVoiceMode, useSpeechRecognitionSupported } from '../../hooks/useVoiceMode';
import VoicePermissionPrompt from './VoicePermissionPrompt';
import { parseContentWithCode } from '../../utils/parseContentWithCode';
import AssistantSidebar from './AssistantSidebar';
import { useTheme } from '../../context/ThemeContext';
import {
  ASSISTANT_MULTI_IMAGE_UPLOAD_ENABLED,
  ASSISTANT_MAX_IMAGE_UPLOAD_COUNT,
} from '../../config/assistantApi';
import { ASSISTANT_API_BASE, ASSISTANT_API_PREFIX } from '../../config/assistantApi';

// V1 texte uniquement : aucune fonctionnalité d'upload image.
const IMAGE_UPLOAD_V1_ENABLED = false;
/** Hauteur max du champ de saisie (px) — alignée avec resizeTextarea */
const INPUT_MAX_HEIGHT_PX = 160;
const MOBILE_BREAKPOINT = '(max-width: 767px)';

const QUICK_SUGGESTIONS = [
  'Comment authentifier mon application ?',
  'Comment créer un paiement ?',
  'Comment configurer les webhooks ?',
  'Quels sont les codes d\'erreur courants ?',
];

const API_ESCALATE = `${ASSISTANT_API_BASE}${ASSISTANT_API_PREFIX}/escalate/tickets`;

function getExportFilename(ext) {
  var dt = new Date();
  var pad = function (n) { return String(n).padStart(2, '0'); };
  var stamp = dt.getFullYear() + '-' + pad(dt.getMonth() + 1) + '-' + pad(dt.getDate()) + '_' + pad(dt.getHours()) + '-' + pad(dt.getMinutes());
  return 'netikash-assistant-' + stamp + '.' + ext;
}

function roleLabel(role) {
  if (role === 'assistant') return 'Assistant';
  if (role === 'user') return 'Utilisateur';
  return 'Système';
}

function getGreetingText() {
  var h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function getDisplayNameFromEmail(userEmail) {
  var s = String(userEmail || '').trim();
  if (!s) return 'Developer';
  var at = s.indexOf('@');
  if (at > 0) return s.slice(0, at);
  return s.slice(0, 18);
}

function buildConversationText(messages) {
  var lines = [];
  (messages || []).forEach(function (m) {
    if (!m || !m.content || m.streaming) return;
    if (m.role !== 'assistant' && m.role !== 'user') return;
    lines.push('[' + roleLabel(m.role) + ']');
    lines.push(String(m.content));
    lines.push('');
  });
  return lines.join('\n').trim() + '\n';
}

function buildConversationMarkdown(messages) {
  var lines = ['# Conversation Netikash AI', ''];
  (messages || []).forEach(function (m) {
    if (!m || !m.content || m.streaming) return;
    if (m.role !== 'assistant' && m.role !== 'user') return;
    lines.push('## ' + roleLabel(m.role));
    lines.push('');
    lines.push(String(m.content));
    lines.push('');
  });
  return lines.join('\n').trim() + '\n';
}

function downloadBlob(content, mimeType, filename) {
  var blob = new Blob([content], { type: mimeType });
  var url = URL.createObjectURL(blob);
  var link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function extractCodeBlocks(messages) {
  const blocks = [];
  messages.forEach((msg) => {
    if (msg.role !== 'assistant') return;
    const content = msg.content || '';
    const parts = parseContentWithCode(content);
    parts.forEach((p) => {
      if (p.type === 'code' && p.value) blocks.push({ code: p.value, language: p.language });
    });
    (msg.snippets || []).forEach((s) => {
      if (s.code) blocks.push({ code: s.code, language: s.language || 'text' });
    });
  });
  return blocks;
}

export default function AssistantChat({ compact = false, commandActionsRef }) {
  const { theme } = useTheme();
  const UI_VARIANT = theme === 'dark' ? 'dark' : 'light';
  const chat = useAssistantThreads();
  const speechSupported = useSpeechRecognitionSupported();
  const {
    messages,
    setMessages,
    loading,
    waitingForSupport,
    setWaitingForSupport,
    sessionId,
    pageUrl,
    runStream,
    userEmail,
    setUserEmail,
    abortStream,
    conversations,
    activeConversationId,
    createConversation,
    switchConversation,
    renameConversation,
    deleteConversation,
  } = chat;

  const handleSelectConversation = (threadId) => {
    // V1 : on évite de switcher pendant un stream pour ne pas mélanger les mises à jour.
    if (loading) return;
    switchConversation(threadId);
  };
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const [isVoiceMode, setIsVoiceMode] = useState(false);
  // Mode conversation : démarré une seule fois, puis on écoute automatiquement
  // après chaque réponse vocale de l'assistant.
  const [isConversationActive, setIsConversationActive] = useState(false);
  const isConversationActiveRef = useRef(false);
  useEffect(() => {
    isConversationActiveRef.current = isConversationActive;
  }, [isConversationActive]);

  const [input, setInput] = useState('');
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [supportMessage, setSupportMessage] = useState('');
  const [supportFocus, setSupportFocus] = useState('message'); // 'email' | 'message'
  const supportMessageRef = useRef(null);
  const supportEmailRef = useRef(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imageError, setImageError] = useState('');
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [showPermissionPrompt, setShowPermissionPrompt] = useState(false);
  const [hasRequestedPermission, setHasRequestedPermission] = useState(false);
  const fileInputRef = useRef(null);
  const exportMenuRef = useRef(null);
  const exportButtonRef = useRef(null);

  useEffect(() => {
    if (!supportModalOpen) return;
    var t = setTimeout(() => {
      try {
        if (supportFocus === 'email') {
          if (supportEmailRef.current) supportEmailRef.current.focus();
        } else {
          if (supportMessageRef.current) supportMessageRef.current.focus();
        }
      } catch (_) {}
    }, 50);
    return () => clearTimeout(t);
  }, [supportModalOpen, supportFocus]);

  const [isMobileLayout, setIsMobileLayout] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    var mq = window.matchMedia(MOBILE_BREAKPOINT);
    var apply = function () {
      setIsMobileLayout(mq.matches);
    };
    apply();
    mq.addEventListener('change', apply);
    return function () {
      mq.removeEventListener('change', apply);
    };
  }, []);

  useEffect(function () {
    if (!isMobileLayout) setMobileSidebarOpen(false);
  }, [isMobileLayout]);

  const mobileDrawerRef = useRef(null);
  const mobileMenuButtonRef = useRef(null);
  const wasMobileDrawerOpenRef = useRef(false);

  /** Verrouille le défilement du document quand le drawer mobile est ouvert. */
  useEffect(
    function () {
      if (!isMobileLayout || !mobileSidebarOpen) return undefined;
      var prevOverflow = document.body.style.overflow;
      var prevPaddingRight = document.body.style.paddingRight;
      var w = typeof window !== 'undefined' ? window.innerWidth - document.documentElement.clientWidth : 0;
      document.body.style.overflow = 'hidden';
      if (w > 0) document.body.style.paddingRight = w + 'px';
      return function () {
        document.body.style.overflow = prevOverflow;
        document.body.style.paddingRight = prevPaddingRight;
      };
    },
    [isMobileLayout, mobileSidebarOpen]
  );

  /** Piège le focus (Tab) dans le drawer + Escape pour fermer ; focus initial sur le 1er contrôle. */
  useEffect(
    function () {
      if (!isMobileLayout || !mobileSidebarOpen) return undefined;
      var root = mobileDrawerRef.current;
      if (!root) return undefined;

      function listFocusables() {
        var sel =
          'button:not([disabled]), [href]:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
        return Array.prototype.slice.call(root.querySelectorAll(sel)).filter(function (el) {
          return el.offsetParent !== null || el === document.activeElement;
        });
      }

      var onKeyDown = function (e) {
        if (e.key === 'Escape') {
          e.preventDefault();
          setMobileSidebarOpen(false);
          return;
        }
        if (e.key !== 'Tab') return;
        var focusables = listFocusables();
        if (!focusables.length) return;
        var first = focusables[0];
        var last = focusables[focusables.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      };

      document.addEventListener('keydown', onKeyDown, true);
      var id = window.requestAnimationFrame(function () {
        var focusables = listFocusables();
        if (focusables[0]) focusables[0].focus();
      });
      return function () {
        document.removeEventListener('keydown', onKeyDown, true);
        window.cancelAnimationFrame(id);
      };
    },
    [isMobileLayout, mobileSidebarOpen]
  );

  /** Reprend le focus sur le bouton « menu » après fermeture du drawer (mobile). */
  useEffect(
    function () {
      if (wasMobileDrawerOpenRef.current && !mobileSidebarOpen && isMobileLayout) {
        window.requestAnimationFrame(function () {
          if (mobileMenuButtonRef.current) mobileMenuButtonRef.current.focus();
        });
      }
      wasMobileDrawerOpenRef.current = mobileSidebarOpen;
    },
    [mobileSidebarOpen, isMobileLayout]
  );

  // Pour éviter de relire / de répéter au fur et à mesure (stream),
  // on parle uniquement le "diff" basé sur le texte sanitizé.
  const spokenSanitizedRef = useRef('');
  const lastSpokeAtRef = useRef(0);
  const lastUtteranceKindRef = useRef('none'); // 'partial' | 'final' | 'prompt' | 'goodbye'
  const voiceApiRef = useRef(null);
  const promptTimeoutRef = useRef(null);
  const PROMPT_ANOTHER_QUESTION =
    "Avez-vous une autre question ? Souhaitez-vous une explication sur un point en particulier ?";
  const GOODBYE_TEXT =
    "J'espère que notre échange a été constructif. Merci. Si vous voulez, posez-moi une autre question quand vous le souhaitez.";

  function stripQuestionIntrosForTTS(text) {
    var t = (text || '').trim();
    if (!t) return '';
    // Si la toute première phrase est un préambule méta ("je prends ... votre question"),
    // on la retire pour démarrer directement l'explication.
    var m = t.match(/^.{0,160}?[.!?]\s+/);
    if (!m) return t;
    var first = m[0] || '';
    var looksMeta =
      /question/i.test(first) &&
      /(prend|en compte|reçu|recu|je prends|j'ai pris|comprend|bien compris|reformule|je prends comme)/i.test(first);
    if (!looksMeta) return t;
    var rest = t.slice(first.length).trim();
    return rest || t;
  }

  async function createSupportTicketFromUI() {
    if (!sessionId) return;
    var msg = String(supportMessage || '').trim();
    if (!msg) return;
    if (loading) return;

    var userMsgId = 'msg_support_req_' + Date.now();
    setMessages(function (prev) {
      return [
        ...(prev || []),
        { id: userMsgId, role: 'user', content: msg, links: [], snippets: [], streaming: false },
        {
          id: 'msg_support_ack_' + Date.now(),
          role: 'assistant',
          content:
            "Je transmets votre demande à notre équipe support.\n\nLa réponse s’affichera automatiquement ici dès qu’elle sera disponible.",
          links: [],
          snippets: [],
          streaming: false,
        },
      ];
    });
    setSupportModalOpen(false);
    setSupportMessage('');
    setWaitingForSupport(true);

    try {
      await fetch(API_ESCALATE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: msg,
          page_url: pageUrl || window.location.pathname,
          page_context: 'assistant',
          session_id: sessionId,
          chat_message_id: userMsgId,
          email: userEmail || null,
          recent_exchanges: [],
        }),
      });
    } catch (_) {}
  }

  const voice = useVoiceMode({
    onTranscription: (text) => {
      if (!isConversationActiveRef.current) return;
      if (!text?.trim()) return;

      var tLower = String(text).toLowerCase();
      // Permet de terminer l'échange par commande vocale (au lieu de devoir cliquer).
      // Objectif : éviter que "merci, c'est bon" déclenche une nouvelle réponse LLM.
      if (
        /(merci|merci beaucoup|c'?est bon|c'est bon|au revoir|aurevoir|salut|stop|arr[eê]te|ça suffit|ca suffit|suffisant|suffisante|pour le moment|pour l'instant|pour l'instant|là c'?est bon|là c'est bon)/i.test(tLower)
      ) {
        if (promptTimeoutRef.current) {
          clearTimeout(promptTimeoutRef.current);
          promptTimeoutRef.current = null;
        }
        setIsConversationActive(false);
        isConversationActiveRef.current = false;
        lastUtteranceKindRef.current = 'goodbye';
        spokenSanitizedRef.current = '';
        lastSpokeAtRef.current = 0;
        try { abortStream(); } catch (e) {}
        try { voice.stopListening(); } catch (e) {}
        try { voice.stopSpeaking(); } catch (e) {}
        voice.speakText(GOODBYE_TEXT);
        return;
      }

      // Si une réponse est en cours (stream et/ou TTS), on l'interrompt pour suivre la file
      // et éviter qu'une question plus récente n'arrive pendant que l'ancienne répond.
      if (loading || voice.isSpeaking) {
        try { abortStream(); } catch (e) {}
        try { voice.stopSpeaking(); } catch (e) {}
        try { voice.stopListening(); } catch (e) {}
        if (promptTimeoutRef.current) {
          clearTimeout(promptTimeoutRef.current);
          promptTimeoutRef.current = null;
        }
      }

      spokenSanitizedRef.current = '';
      lastSpokeAtRef.current = 0;
      const history = messages
        .filter((m) => (m.role === 'user' || m.role === 'assistant') && m.content && !m.streaming)
        .slice(-10)
        .map((m) => ({ role: m.role, content: m.content }));
      setMessages((prev) => [...prev, { role: 'user', content: text.trim() }]);
      runStream(
        text.trim(),
        history,
        (finalContent, links, snippets) => {
          const hasCode = (snippets || []).length > 0;
          let finalSanitized = voice.getTextOnlyForTTS(finalContent, { hasCode: hasCode, force: true });
          // Si la réponse LLM ne contient que du code (content vide mais snippets non vides),
          // `getTextOnlyForTTS` peut renvoyer '' : on garantit une phrase lisible.
          if (!finalSanitized && hasCode) {
            finalSanitized =
              'Le code correspondant est affiché à l’écran. Pour les détails, lisez-le visuellement.';
          }
          finalSanitized = stripQuestionIntrosForTTS(finalSanitized);
          const spoken = spokenSanitizedRef.current || '';
          const remaining =
            finalSanitized && spoken && finalSanitized.startsWith(spoken)
              ? finalSanitized.slice(spoken.length).trim()
              : (finalSanitized || '').trim();
          if (voice.isListening) voice.stopListening();
          lastUtteranceKindRef.current = 'final';
          if (remaining) voice.speakText(remaining);
          // Cas limite : si tout a déjà été énoncé via partial,
          // le "remaining" peut être vide. On relance quand même.
          if (!remaining && !voice.isSpeaking) {
            lastUtteranceKindRef.current = 'prompt';
            voice.speakText(PROMPT_ANOTHER_QUESTION);
          }
          spokenSanitizedRef.current = finalSanitized || '';
        },
        (partialContent) => {
          var partialSanitized = voice.getTextOnlyForTTS(partialContent, { hasCode: false });
          if (!partialSanitized) return;
          // Si on parle pour la "première fois" dans cette réponse,
          // on retire les préambules méta éventuels.
          if (!spokenSanitizedRef.current) {
            partialSanitized = stripQuestionIntrosForTTS(partialSanitized);
          }
          const spoken = spokenSanitizedRef.current || '';
          if (!partialSanitized.startsWith(spoken)) {
            spokenSanitizedRef.current = '';
            return;
          }
          const newPart = partialSanitized.slice(spoken.length).trim();
          if (!newPart) return;

          // On parle seulement quand on a une ponctuation claire (ou à défaut un minimum de longueur).
          const hasSentenceEnd = /[.!?]\s|[.!?]$/.test(newPart);
          if (!hasSentenceEnd && newPart.length < 60) return;

          var now = Date.now();
          if (now - lastSpokeAtRef.current < 1200) return;
          if (voice.isListening) voice.stopListening();
          lastUtteranceKindRef.current = 'partial';
          voice.speakText(newPart);
          spokenSanitizedRef.current = spoken + newPart;
          lastSpokeAtRef.current = now;
        },
        null
      );
    },
    onSpeakEnd: () => {
      const v = voiceApiRef.current;
      if (!v) return;

      if (lastUtteranceKindRef.current === 'final') {
        // Fin de réponse : relance vocale, puis on repasse en écoute.
        if (!isConversationActiveRef.current) {
          lastUtteranceKindRef.current = 'none';
          return;
        }
        // Petite attente pour laisser la reconnaissance vocale capter d'éventuels mots
        // comme "merci" juste après la fin de la phrase.
        if (promptTimeoutRef.current) clearTimeout(promptTimeoutRef.current);
        promptTimeoutRef.current = setTimeout(function () {
          if (!isConversationActiveRef.current) return;
          lastUtteranceKindRef.current = 'prompt';
          v.stopListening();
          v.speakText(PROMPT_ANOTHER_QUESTION);
          promptTimeoutRef.current = null;
        }, 350);
      } else if (lastUtteranceKindRef.current === 'prompt') {
        lastUtteranceKindRef.current = 'none';
        if (isConversationActiveRef.current) {
          // Sécurité : ne redémarre pas la reconnaissance tant que la synthèse n'est pas vraiment finie.
          var ss = window && window.speechSynthesis ? window.speechSynthesis : null;
          if (ss && (ss.speaking || ss.paused)) {
            setTimeout(function () {
              var ss2 = window && window.speechSynthesis ? window.speechSynthesis : null;
              if (ss2 && (ss2.speaking || ss2.paused)) return;
              if (isConversationActiveRef.current) v.startListening();
            }, 180);
            return;
          }
          v.startListening();
        }
      } else if (lastUtteranceKindRef.current === 'goodbye') {
        lastUtteranceKindRef.current = 'none';
      }
    }
  });
  useEffect(() => {
    voiceApiRef.current = voice;
  }, [voice]);

  const sendMessage = useCallback((text) => {
    const t = (text || '').trim();
    if (!t || loading) return;
    const tLower = String(t).toLowerCase();
    const isClosingIntent =
      /(merci|merci beaucoup|c'?est bon|c'est bon|au revoir|aurevoir|stop|arr[eê]te|ça suffit|ca suffit|pour le moment|pour l'instant|suffisant|suffisante)/i.test(tLower) &&
      !/(\?|comment |pourquoi |où |ou |quand |quel |quelle |cr[eée]er |payer |paiement |api |endpoint |webhook)/i.test(tLower);
    if (isClosingIntent) {
      var reply =
        /(pour le moment|pour l'instant|suffisant|suffisante)/i.test(tLower)
          ? "Parfait. On s'arrête ici. Posez une nouvelle question quand vous voulez."
          : "Avec plaisir. Si vous le souhaitez, on s'arrête ici.";
      const outgoingAttachments = IMAGE_UPLOAD_V1_ENABLED
        ? selectedImages.map((img) => ({
            name: img.name || 'image',
            dataUrl: img.dataUrl,
            sensitiveWarning: !!img.sensitiveWarning,
          }))
        : [];
      setMessages(function (prev) {
        var now = Date.now();
        return [
          ...(prev || []),
          { role: 'user', content: t, attachments: outgoingAttachments },
          { id: 'msg_' + now, role: 'assistant', content: reply, links: [], snippets: [], streaming: false },
        ];
      });
      if (IMAGE_UPLOAD_V1_ENABLED && selectedImages.length > 0) setSelectedImages([]);
      return;
    }
    spokenSanitizedRef.current = '';
    lastSpokeAtRef.current = 0;
    const history = messages
      .filter((m) => (m.role === 'user' || m.role === 'assistant') && m.content && !m.streaming)
      .slice(-10)
      .map((m) => ({ role: m.role, content: m.content }));
    const outgoingAttachments = IMAGE_UPLOAD_V1_ENABLED
      ? selectedImages.map((img) => ({
          name: img.name || 'image',
          dataUrl: img.dataUrl,
          sensitiveWarning: !!img.sensitiveWarning,
        }))
      : [];
    setMessages((prev) => [...prev, { role: 'user', content: t, attachments: outgoingAttachments }]);
    if (isVoiceMode) {
      runStream(
        t,
        history,
        (finalContent, links, snippets) => {
          const hasCode = (snippets || []).length > 0;
          let finalSanitized = voice.getTextOnlyForTTS(finalContent, { hasCode: hasCode, force: true });
          // Si la réponse LLM ne contient que du code (content vide mais snippets non vides),
          // `getTextOnlyForTTS` peut renvoyer '' : on garantit une phrase lisible.
          if (!finalSanitized && hasCode) {
            finalSanitized =
              'Le code correspondant est affiché à l’écran. Pour les détails, lisez-le visuellement.';
          }
          finalSanitized = stripQuestionIntrosForTTS(finalSanitized);
          const spoken = spokenSanitizedRef.current || '';
          const remaining =
            finalSanitized && spoken && finalSanitized.startsWith(spoken)
              ? finalSanitized.slice(spoken.length).trim()
              : (finalSanitized || '').trim();
          if (voice.isListening) voice.stopListening();
          lastUtteranceKindRef.current = 'final';
          if (remaining) voice.speakText(remaining);
          // Cas limite : relance même si le remaining est vide.
          if (!remaining && !voice.isSpeaking) {
            lastUtteranceKindRef.current = 'prompt';
            voice.speakText(PROMPT_ANOTHER_QUESTION);
          }
          spokenSanitizedRef.current = finalSanitized || '';
        },
        (partialContent) => {
          const partialSanitized = voice.getTextOnlyForTTS(partialContent, { hasCode: false });
          if (!partialSanitized) return;
          const spoken = spokenSanitizedRef.current || '';
          if (!partialSanitized.startsWith(spoken)) {
            spokenSanitizedRef.current = '';
            return;
          }
          const newPart = partialSanitized.slice(spoken.length).trim();
          if (!newPart) return;

          const hasSentenceEnd = /[.!?]\s|[.!?]$/.test(newPart);
          if (!hasSentenceEnd && newPart.length < 80) return;

          var now = Date.now();
          if (now - lastSpokeAtRef.current < 1200) return;
          if (voice.isListening) voice.stopListening();
          lastUtteranceKindRef.current = 'partial';
          voice.speakText(newPart);
          spokenSanitizedRef.current = spoken + newPart;
          lastSpokeAtRef.current = now;
        },
        IMAGE_UPLOAD_V1_ENABLED && selectedImages.length > 0 ? selectedImages[0].dataUrl : null,
        IMAGE_UPLOAD_V1_ENABLED ? selectedImages.map((img) => img.dataUrl) : null
      );
      if (IMAGE_UPLOAD_V1_ENABLED && selectedImages.length > 0) {
        setSelectedImages([]);
      }
    } else {
      runStream(t, history);
    }
  }, [messages, loading, isVoiceMode, runStream, setMessages, voice, selectedImages]);

  useEffect(() => {
    // En mode vocal, on évite un scroll agressif pendant le streaming,
    // sinon l'utilisateur ne peut pas lire/observer les détails.
    if (isVoiceMode) {
      const hasStreamingMessage = (messages || []).some(function (m) { return !!m.streaming; });
      if (hasStreamingMessage) return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, isVoiceMode]);

  useEffect(() => {
    if (!commandActionsRef) return;
    commandActionsRef.current = {
      sendPrompt: (text) => {
        if (text?.trim() && !loading) sendMessage(text.trim());
      },
      switchToVoice: () => {
        // TEXT-ONLY v1 : mode vocal désactivé (pas de bascule via commande).
        return;
      },
    };
    return () => { commandActionsRef.current = null; };
  }, [commandActionsRef, loading, speechSupported, hasRequestedPermission, voice?.permissionState, sendMessage]);

  const handleSendClick = () => {
    const val = input.trim();
    if (val) {
      sendMessage(val);
      setInput('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendClick();
    }
  };

  const resizeTextarea = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, INPUT_MAX_HEIGHT_PX)}px`;
  };

  useEffect(() => {
    resizeTextarea();
  }, [input]);

  useEffect(() => {
    if (!isExportOpen) return;
    var menuRoot = exportMenuRef.current;
    var menuEl = menuRoot ? menuRoot.querySelector('#assistant-export-menu') : null;
    var items = menuEl
      ? Array.prototype.slice.call(menuEl.querySelectorAll('button[role="menuitem"]'))
      : [];
    var first = items[0];
    var last = items[items.length - 1];
    if (first) first.focus();

    var onClickOutside = function (e) {
      if (!exportMenuRef.current) return;
      if (!exportMenuRef.current.contains(e.target)) {
        setIsExportOpen(false);
      }
    };
    var onKeyDown = function (e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsExportOpen(false);
        if (exportButtonRef.current) exportButtonRef.current.focus();
        return;
      }
      if (e.key !== 'Tab' || !items.length) return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          if (last) last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        if (first) first.focus();
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKeyDown);
    return function () {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isExportOpen]);

  const handleRequestPermission = () => {
    // TEXT-ONLY v1 : mode vocal désactivé.
    return;
  };

  const handleParlerClick = () => {
    // TEXT-ONLY v1 : mode vocal désactivé.
    return;
  };

  const handleSuggestionClick = (textToSend) => {
    var s = String(textToSend || '').trim();
    if (!s || loading) return;
    if (s === 'Laisser un email pour la réponse') {
      setSupportFocus('email');
      setSupportModalOpen(true);
      return;
    }
    if (s === 'Partager l’endpoint concerné' || s === "Partager l'endpoint concerné") {
      setSupportFocus('message');
      setSupportModalOpen(true);
      setSupportMessage(function (prev) {
        var p = String(prev || '').trim();
        if (p) return prev;
        return 'Endpoint concerné : \nErreur / payload : \n';
      });
      return;
    }
    if (s === 'Ajouter une ref/trans_id') {
      setSupportFocus('message');
      setSupportModalOpen(true);
      setSupportMessage(function (prev) {
        var p = String(prev || '').trim();
        if (p) return prev;
        return 'ref : \ntrans_id : \n';
      });
      return;
    }
    sendMessage(s);
  };

  const handleFeedback = (messageId, value) => {
    if (!messageId) return;
    setMessages((prev) =>
      (prev || []).map((m) => {
        if (m.id !== messageId) return m;
        return { ...m, feedback: m.feedback === value ? null : value };
      })
    );
  };

  const handlePlusClick = () => {
    if (IMAGE_UPLOAD_V1_ENABLED) {
      handlePickImage();
      return;
    }
    if (loading) return;
    setInput((v) => (v && v.trim() ? v : QUICK_SUGGESTIONS[0] || ''));
    textareaRef.current?.focus();
  };

  const exportToTxt = () => {
    const text = buildConversationText(messages);
    downloadBlob(text, 'text/plain;charset=utf-8', getExportFilename('txt'));
    setIsExportOpen(false);
  };

  const exportToMarkdown = () => {
    const markdown = buildConversationMarkdown(messages);
    downloadBlob(markdown, 'text/markdown;charset=utf-8', getExportFilename('md'));
    setIsExportOpen(false);
  };

  const exportToPdf = () => {
    const text = buildConversationText(messages);
    const doc = new jsPDF({
      orientation: 'p',
      unit: 'pt',
      format: 'a4',
    });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    const margin = 40;
    const maxWidth = doc.internal.pageSize.getWidth() - margin * 2;
    const wrapped = doc.splitTextToSize(text, maxWidth);
    let y = margin;
    const lineHeight = 16;
    wrapped.forEach((line) => {
      if (y > doc.internal.pageSize.getHeight() - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += lineHeight;
    });
    doc.save(getExportFilename('pdf'));
    setIsExportOpen(false);
  };

  const handlePickImage = () => {
    if (loading) return;
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleImageChange = (e) => {
    const files = e.target && e.target.files ? Array.from(e.target.files) : [];
    if (!files.length) return;
    setImageError('');
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
    const incoming = ASSISTANT_MULTI_IMAGE_UPLOAD_ENABLED ? files : [files[0]];
    const remainingSlots = ASSISTANT_MAX_IMAGE_UPLOAD_COUNT - selectedImages.length;
    if (remainingSlots <= 0) {
      setImageError(`Nombre maximal atteint (${ASSISTANT_MAX_IMAGE_UPLOAD_COUNT} images).`);
      return;
    }
    const toRead = incoming.slice(0, remainingSlots);
    const sensitiveNameRegex = /(passport|id[_-]?card|identity|cni|nif|bank|rib|iban|swift|carte|credit|cvv)/i;
    Promise.all(toRead.map((file) => new Promise((resolve) => {
      if (!allowed.includes(file.type)) {
        resolve({ error: 'Format non supporté. Utilisez PNG, JPG, WEBP ou GIF.' });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        resolve({ error: 'Image trop volumineuse (max 5 MB).' });
        return;
      }
      const reader = new FileReader();
      reader.onload = function () {
        const result = typeof reader.result === 'string' ? reader.result : '';
        if (!result.startsWith('data:image/')) {
          resolve({ error: 'Le fichier sélectionné ne peut pas être lu comme image.' });
          return;
        }
        resolve({
          dataUrl: result,
          name: file.name || 'image',
          sensitiveWarning: sensitiveNameRegex.test(file.name || ''),
        });
      };
      reader.readAsDataURL(file);
    }))).then((results) => {
      const valid = results.filter((r) => !!r.dataUrl);
      const firstError = results.find((r) => !!r.error);
      if (firstError) setImageError(firstError.error);
      if (valid.length) setSelectedImages((prev) => prev.concat(valid));
    });
    // reset input to allow selecting same file again
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const codeBlocks = extractCodeBlocks(messages);

  // TEXT-ONLY v1 : mode vocal désactivé
  if (false && isVoiceMode) {
    const voiceStateLabel = voice.isSpeaking
      ? 'Je parle...'
      : voice.isListening
        ? 'En écoute...'
        : 'Prêt';

    return (
      <div className="fixed inset-0 z-[200] bg-slate-950 text-slate-100 flex flex-col overflow-hidden">
        <div className="flex flex-col flex-1 min-h-0">
          {/* Gauche : conversation + (optionnel) code */}
          <div className="flex flex-col gap-6 min-h-0">
            <div className="flex-1 min-h-0 rounded-3xl border border-slate-700/50 bg-slate-900/10 backdrop-blur overflow-hidden flex flex-col">
              <div className="px-6 py-4 border-b border-slate-700/40 bg-slate-900/20 flex items-center justify-between">
                <div>
                  <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-[0.2em]">Conversation</div>
                  <div className="mt-2">
                    <span
                      className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[12px] border transition-all duration-300 ${
                        voice.isSpeaking
                          ? 'border-amber-400/40 bg-amber-400/10 text-amber-300'
                          : isConversationActive
                            ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                            : 'border-slate-600/60 bg-slate-800/40 text-slate-300'
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          voice.isSpeaking
                            ? 'bg-amber-400 animate-pulse'
                            : isConversationActive
                              ? 'bg-emerald-400 animate-pulse'
                              : 'bg-slate-400'
                        }`}
                      />
                      {voiceStateLabel}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsVoiceMode(false)}
                  className="p-2 rounded-xl text-slate-300 hover:text-white hover:bg-slate-800/40 transition-all duration-200"
                  aria-label="Retour au mode texte"
                >
                  <Close sx={{ fontSize: 20 }} />
                </button>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto p-6 bg-transparent">
                {messages.length === 0 && (
                  <div className="h-full flex flex-col items-center justify-center text-center">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400/20 to-amber-600/10 flex items-center justify-center mb-4 ring-1 ring-amber-400/20">
                      <Mic sx={{ fontSize: 22 }} />
                    </div>
                    <p className="text-slate-200 font-semibold text-lg mb-2">Mode vocal</p>
                    <p className="text-slate-400 text-[14px] max-w-md leading-relaxed">
                      Appuyez sur le micro. Je réponds et le code s&apos;affiche sans être lu.
                    </p>
                  </div>
                )}

                {messages.map((msg, i) => (
                  <div key={msg.id || `m${i}`} className="assistant-msg-enter">
                    <MessageBubble
                      message={msg}
                      isUser={msg.role === 'user'}
                      uiVariant={UI_VARIANT}
                      onSuggestionClick={handleSuggestionClick}
                      onFeedback={(value) => handleFeedback(msg.id, value)}
                    />
                  </div>
                ))}

                {loading && (
                  <div className="flex justify-start mb-4 assistant-msg-enter">
                    <div className="px-5 py-3 rounded-2xl rounded-bl-md bg-slate-800/60 text-slate-300 text-[15px] flex items-center gap-3 border border-slate-700/60 ring-1 ring-amber-400/10">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" style={{ animationDelay: '300ms' }} />
                      </div>
                      Réflexion...
                    </div>
                  </div>
                )}

                {waitingForSupport && (
                  <div className="flex justify-start mb-4 assistant-msg-enter">
                    <div className="px-5 py-3 rounded-2xl rounded-bl-md bg-amber-400/10 text-amber-300/95 text-[15px] border border-amber-400/20">
                      <div className="flex flex-col gap-1">
                        <span>Transmis au support. Réponse sous 24 h ouvrées.</span>
                        <span className="text-[12px] text-amber-200/80">
                          La réponse s’affichera automatiquement ici dès qu’elle sera disponible.
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              <div className="shrink-0 p-4 border-t border-slate-700/40 bg-slate-900/5">
                <div className="flex items-center justify-center">
                  <button
                    type="button"
                    onClick={handleParlerClick}
                    disabled={voice.permissionState === 'denied' || !speechSupported}
                    className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-200 border ${
                      isConversationActive
                        ? 'bg-red-500/90 text-white border-red-500/40 shadow-[0_8px_30px_-10px_rgba(239,68,68,0.55)]'
                        : 'bg-[#2a2a2a] text-slate-200 border-white/10 hover:bg-white/5'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    aria-label={isConversationActive ? 'Arrêter le vocal' : 'Parler'}
                  >
                    <Mic sx={{ fontSize: 22 }} />
                  </button>
                </div>
                <p className="text-center text-[12px] text-slate-400 mt-2">
                  {isConversationActive ? 'Touchez pour terminer la conversation vocale' : 'Touchez pour démarrer la conversation vocale'}
                </p>
              </div>
            </div>

            {codeBlocks.length > 0 && (
              <div className="rounded-3xl border border-slate-700/40 bg-slate-900/5 overflow-hidden">
                <div className="px-6 py-3 border-b border-slate-700/30 bg-slate-900/15">
                  <h2 className="text-[11px] font-semibold text-slate-400 uppercase tracking-[0.2em]">Code</h2>
                </div>
                <div className="p-4 space-y-4 max-h-[240px] overflow-y-auto">
                  {codeBlocks.map((blk, i) => (
                    <CodeBlockWithCopy key={i} code={blk.code} language={blk.language} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {showPermissionPrompt && (
          <VoicePermissionPrompt
            onRequest={handleRequestPermission}
            onDismiss={() => {
              setShowPermissionPrompt(false);
              setHasRequestedPermission(true);
            }}
          />
        )}
      </div>
    );
  }

  // Mode texte : zone agrandie, textarea auto-resize, suggestions rapides
  return (
    <div className="w-full h-full flex min-h-0 overflow-hidden relative">
      {isMobileLayout && mobileSidebarOpen && (
        <button
          type="button"
          tabIndex={-1}
          className="fixed inset-0 z-[35] bg-slate-900/40 backdrop-blur-[2px] transition-opacity"
          aria-label="Fermer le menu des discussions"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}
      <div
        ref={mobileDrawerRef}
        className={
          isMobileLayout
            ? mobileSidebarOpen
              ? 'assistant-sidebar-drawer fixed left-0 top-0 bottom-0 z-40 flex max-w-full min-h-0'
              : 'hidden'
            : 'flex shrink-0 h-full min-h-0'
        }
        aria-hidden={isMobileLayout ? !mobileSidebarOpen : undefined}
        aria-modal={isMobileLayout && mobileSidebarOpen ? true : undefined}
        role={isMobileLayout && mobileSidebarOpen ? 'dialog' : undefined}
        aria-label={isMobileLayout && mobileSidebarOpen ? 'Discussions' : undefined}
      >
        <AssistantSidebar
          conversations={conversations}
          activeConversationId={activeConversationId}
          onNewConversation={createConversation}
          onSelectConversation={handleSelectConversation}
          onRenameConversation={renameConversation}
          onDeleteConversation={deleteConversation}
          onInteraction={() => setMobileSidebarOpen(false)}
        />
      </div>

      <div className="flex-1 overflow-hidden flex flex-col min-h-0 min-w-0">
        {isMobileLayout && (
          <div className="shrink-0 flex items-center gap-3 px-3 py-2 border-b border-slate-200/90 bg-white/60 backdrop-blur-sm">
            <button
              ref={mobileMenuButtonRef}
              type="button"
              onClick={() => setMobileSidebarOpen(true)}
              className="inline-flex h-11 w-11 min-w-[44px] min-h-[44px] items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50 transition-colors"
              aria-label="Ouvrir les discussions"
              aria-expanded={mobileSidebarOpen}
              aria-controls="assistant-discussions-sidebar"
            >
              <Menu sx={{ fontSize: 22 }} />
            </button>
            <span className="text-sm font-semibold text-slate-800 truncate">Discussions</span>
          </div>
        )}
        <div
          className="flex-1 overflow-y-auto px-4 sm:px-6 pt-6 pb-4 min-h-0 text-[15px]"
          role="log"
          aria-live={loading ? 'off' : 'polite'}
          aria-relevant="additions text"
          aria-label="Historique de conversation"
        >
          <div className="w-full max-w-3xl mx-auto mb-4 flex justify-end">
            <div className="relative" ref={exportMenuRef}>
              <button
                ref={exportButtonRef}
                type="button"
                onClick={() => setIsExportOpen((v) => !v)}
                className="h-9 px-3 rounded-xl border border-slate-200 bg-white/70 text-slate-700 text-sm hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/60 transition-colors dark:border-slate-600 dark:bg-slate-800/90 dark:text-slate-200 dark:hover:bg-slate-700"
                aria-label="Exporter la conversation"
                aria-expanded={isExportOpen}
                aria-haspopup="menu"
                aria-controls="assistant-export-menu"
              >
                Exporter
              </button>
              {isExportOpen && (
                <div
                  id="assistant-export-menu"
                  role="menu"
                  aria-label="Options d'export"
                  className="absolute right-0 mt-2 w-44 rounded-xl border border-slate-200 bg-white/95 backdrop-blur p-1 shadow-lg z-20 dark:border-slate-600 dark:bg-slate-800/98"
                >
                  <button
                    type="button"
                    onClick={exportToTxt}
                    role="menuitem"
                    className="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/60 transition-colors dark:text-slate-200 dark:hover:bg-slate-700/80"
                  >
                    Export TXT
                  </button>
                  <button
                    type="button"
                    onClick={exportToMarkdown}
                    role="menuitem"
                    className="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/60 transition-colors dark:text-slate-200 dark:hover:bg-slate-700/80"
                  >
                    Export Markdown
                  </button>
                  <button
                    type="button"
                    onClick={exportToPdf}
                    role="menuitem"
                    className="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/60 transition-colors dark:text-slate-200 dark:hover:bg-slate-700/80"
                  >
                    Export PDF
                  </button>
                </div>
              )}
            </div>
          </div>
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-start text-center">
              <div className="mt-10 max-w-xl px-1">
                <p
                  className={
                    'text-[22px] leading-tight font-semibold tracking-tight ' +
                    (UI_VARIANT === 'light' ? 'text-slate-900' : 'text-slate-100')
                  }
                >
                  {getGreetingText()}, {getDisplayNameFromEmail(userEmail)}
                </p>
                <p
                  className={
                    'text-[13px] mt-2 font-medium ' +
                    (UI_VARIANT === 'light' ? 'text-slate-500' : 'text-slate-400')
                  }
                >
                  How can I assist you today?
                </p>

                <div className="mt-8 flex flex-wrap justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => sendMessage("Explique-moi étape par étape, en restant strictement sur la documentation, comment faire ma première intégration Netikash.")}
                    disabled={loading}
                    className={
                      'h-10 px-4 rounded-xl border font-semibold transition-all ' +
                      (UI_VARIANT === 'light'
                        ? 'bg-white border-slate-200 text-slate-800 shadow-sm hover:bg-slate-50 hover:border-amber-300/50 disabled:opacity-50'
                        : 'bg-slate-800/80 border-slate-600 text-slate-100 hover:bg-slate-700 disabled:opacity-50')
                    }
                  >
                    Reasoning
                  </button>

                  <button
                    type="button"
                    disabled
                    className={
                      'h-10 px-4 rounded-xl border font-semibold cursor-not-allowed ' +
                      (UI_VARIANT === 'light'
                        ? 'bg-slate-50 border-slate-200 text-slate-400 opacity-70'
                        : 'bg-slate-900/50 border-slate-700 text-slate-500 opacity-70')
                    }
                    title="Bientôt (V2)"
                    aria-disabled="true"
                  >
                    Create Image
                  </button>

                  <button
                    type="button"
                    disabled
                    className={
                      'h-10 px-4 rounded-xl border font-semibold cursor-not-allowed ' +
                      (UI_VARIANT === 'light'
                        ? 'bg-slate-50 border-slate-200 text-slate-400 opacity-70'
                        : 'bg-slate-900/50 border-slate-700 text-slate-500 opacity-70')
                    }
                    title="Bientôt (V2)"
                    aria-disabled="true"
                  >
                    Deep Research
                  </button>
                </div>

                
              </div>
            </div>
          )}

          {(
            messages.length > 100 ? messages.slice(-120) : messages
          ).map((msg, i) => (
            <div
              key={msg.id || `m${i}`}
              className={msg.role === 'user' ? 'assistant-msg-enter assistant-msg-enter-user' : 'assistant-msg-enter'}
            >
              <MessageBubble
                message={msg}
                isUser={msg.role === 'user'}
                uiVariant={UI_VARIANT}
                onSuggestionClick={handleSuggestionClick}
                onFeedback={(value) => handleFeedback(msg.id, value)}
              />
            </div>
          ))}
          {loading && (
            <div className="flex justify-start mb-4 assistant-msg-enter">
              <div
                className={
                  'px-5 py-3.5 rounded-2xl rounded-bl-md flex items-center gap-3 border text-[13px] ' +
                  (UI_VARIANT === 'light'
                    ? 'bg-slate-50 border-slate-200 text-slate-600'
                    : 'bg-slate-800/70 border-slate-600/70 text-slate-300')
                }
                role="status"
                aria-live="polite"
              >
                <div className="flex gap-1.5 items-center" aria-hidden="true">
                  <span
                    className={
                      'assistant-typing-dot w-1.5 h-1.5 rounded-full ' +
                      (UI_VARIANT === 'light' ? 'bg-amber-500' : 'bg-amber-400')
                    }
                    style={{ animationDelay: '0ms' }}
                  />
                  <span
                    className={
                      'assistant-typing-dot w-1.5 h-1.5 rounded-full ' +
                      (UI_VARIANT === 'light' ? 'bg-amber-500' : 'bg-amber-400')
                    }
                    style={{ animationDelay: '160ms' }}
                  />
                  <span
                    className={
                      'assistant-typing-dot w-1.5 h-1.5 rounded-full ' +
                      (UI_VARIANT === 'light' ? 'bg-amber-500' : 'bg-amber-400')
                    }
                    style={{ animationDelay: '320ms' }}
                  />
                </div>
                <span className={UI_VARIANT === 'light' ? 'text-slate-500' : 'text-slate-400'}>
                  L&apos;assistant rédige…
                </span>
              </div>
            </div>
          )}
          {waitingForSupport && (
            <div className="flex justify-start mb-4 assistant-msg-enter">
              <div
                className={
                  'px-5 py-3 rounded-2xl rounded-bl-md text-[13px] border ' +
                  (UI_VARIANT === 'light'
                    ? 'bg-amber-50 text-amber-900 border-amber-200/80'
                    : 'bg-amber-500/12 text-amber-200 border-amber-400/25')
                }
                role="status"
                aria-live="polite"
              >
                <div className="flex flex-col gap-1">
                  <span>Transmis au support. Réponse sous 24 h ouvrées.</span>
                  <span
                    className={
                      'text-[12px] ' + (UI_VARIANT === 'light' ? 'text-amber-800/80' : 'text-amber-200/80')
                    }
                  >
                    La réponse s’affichera automatiquement ici dès qu’elle sera disponible.
                  </span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="shrink-0 border-t border-slate-200/90 bg-gradient-to-b from-slate-50/90 to-white px-4 sm:px-6 pb-6 pt-5 dark:border-slate-700/80 dark:from-slate-900/80 dark:to-slate-950">
          {IMAGE_UPLOAD_V1_ENABLED && selectedImages.length > 0 && (
            <div className="w-full max-w-3xl mx-auto mb-3">
              <div className="rounded-2xl border border-white/10 bg-slate-900/30 p-2.5">
                <div className="space-y-2">
                  {selectedImages.map((img, idx) => (
                    <div key={`${img.name}-${idx}`} className="flex items-center gap-3">
                      <img
                        src={img.dataUrl}
                        alt={`Aperçu ${img.name}`}
                        className="w-12 h-12 rounded-lg object-cover border border-white/10"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-200 text-sm truncate">{img.name || 'Image sélectionnée'}</p>
                        <p className={`text-xs ${img.sensitiveWarning ? 'text-amber-300' : 'text-slate-400'}`}>
                          {img.sensitiveWarning ? 'Attention: image potentiellement sensible.' : 'Envoyée avec votre prochaine question'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedImages((prev) => prev.filter((_, i) => i !== idx))}
                        className="px-2.5 py-1 rounded-lg text-xs text-slate-300 hover:text-white hover:bg-slate-800/60 transition-colors"
                        aria-label={`Retirer l'image ${img.name}`}
                      >
                        Retirer
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="w-full max-w-3xl mx-auto bg-white rounded-[20px] px-4 min-h-12 py-2 flex items-end gap-2 shadow-[0_4px_24px_-12px_rgba(15,23,42,0.1)] border border-slate-200/90 ring-1 ring-slate-900/[0.04] transition-shadow duration-200 focus-within:ring-2 focus-within:ring-amber-400/45 focus-within:border-amber-300/50 focus-within:shadow-[0_8px_32px_-14px_rgba(234,179,63,0.14)] dark:border-slate-600 dark:bg-slate-800/95 dark:ring-slate-900/40">
            <button
              type="button"
              onClick={handlePlusClick}
              disabled={loading}
              className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/60 transition-colors disabled:opacity-50 dark:text-slate-300 dark:hover:bg-slate-700/80"
              aria-label={IMAGE_UPLOAD_V1_ENABLED ? 'Ajouter une image' : 'Ajouter une question'}
              title={IMAGE_UPLOAD_V1_ENABLED ? 'Ajouter une image' : 'Ajouter une question'}
            >
              <span className="text-xl leading-none translate-y-[-1px]">+</span>
            </button>

            <button
              type="button"
              onClick={() => setSupportModalOpen(true)}
              disabled={loading}
              className="h-8 px-3 shrink-0 rounded-full text-[12px] font-semibold border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 hover:border-amber-300/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/60 disabled:opacity-50 dark:bg-slate-900/30 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800/60"
              aria-label="Contacter le support"
              title="Contacter le support"
            >
              Contacter le support
            </button>
            {IMAGE_UPLOAD_V1_ENABLED && (
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                multiple={ASSISTANT_MULTI_IMAGE_UPLOAD_ENABLED}
                className="hidden"
                onChange={handleImageChange}
              />
            )}

            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => { setInput(e.target.value); resizeTextarea(); }}
              onKeyDown={handleKeyDown}
              placeholder="Ex: authentifier, créer un paiement, configurer un webhook…"
              rows={1}
              className="flex-1 bg-transparent text-slate-900 placeholder:text-slate-400 placeholder:italic outline-none border-0 focus:ring-0 focus:border-transparent resize-none text-[15px] py-2 min-h-[0] leading-[22px] max-h-[160px] dark:text-slate-100 dark:placeholder:text-slate-500"
              disabled={loading}
              aria-label="Saisir votre question à l'assistant"
            />

            <button
              type="button"
              onClick={handleSendClick}
              disabled={loading || !input.trim()}
              aria-label="Envoyer"
              title="Envoyer"
              className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/60 ${
                loading || !input.trim()
                  ? 'bg-amber-400/10 text-amber-500/50 cursor-not-allowed dark:bg-amber-500/10 dark:text-amber-500/40'
                  : 'bg-amber-500 text-white hover:bg-amber-400 dark:bg-amber-600 dark:hover:bg-amber-500'
              } disabled:opacity-60`}
            >
              <Send fontSize="small" />
            </button>

            {/* TEXT-ONLY v1 : bouton mode vocal désactivé */}
          </div>
          {IMAGE_UPLOAD_V1_ENABLED && imageError && (
            <p className="w-full max-w-3xl mx-auto mt-2 text-xs text-amber-300">{imageError}</p>
          )}
        </div>
      </div>
      {/* TEXT-ONLY v1 : VoicePermissionPrompt désactivé */}

      {supportModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/40 backdrop-blur-[2px]"
            onClick={() => setSupportModalOpen(false)}
            aria-label="Fermer"
          />
          <div className="relative w-full max-w-lg rounded-3xl border border-slate-200 bg-white shadow-[0_24px_80px_-40px_rgba(15,23,42,0.45)] p-6 dark:border-slate-700 dark:bg-slate-950">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-slate-900 font-semibold tracking-tight text-lg dark:text-slate-100">
                  Contacter le support
                </p>
                <p className="text-slate-500 text-sm mt-1 dark:text-slate-400">
                  Décris ton problème. La réponse s’affichera automatiquement ici dès qu’elle sera disponible.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSupportModalOpen(false)}
                className="w-9 h-9 rounded-full flex items-center justify-center text-slate-600 hover:bg-slate-100 transition-colors dark:text-slate-300 dark:hover:bg-slate-800/70"
                aria-label="Fermer la fenêtre"
              >
                <Close fontSize="small" />
              </button>
            </div>

            <div className="mt-5 space-y-4">
              <div>
                <label className="block text-[12px] font-semibold text-slate-700 dark:text-slate-300">
                  Email (optionnel)
                </label>
                <input
                  ref={supportEmailRef}
                  type="email"
                  value={userEmail || ''}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="ex: dev@exemple.com"
                  className="mt-2 w-full h-11 rounded-2xl border border-slate-200 bg-white px-4 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-amber-300/60 focus:border-amber-300/60 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-100"
                />
              </div>

              <div>
                <label className="block text-[12px] font-semibold text-slate-700 dark:text-slate-300">
                  Message
                </label>
                <textarea
                  ref={supportMessageRef}
                  value={supportMessage}
                  onChange={(e) => setSupportMessage(e.target.value)}
                  rows={5}
                  placeholder="Explique le problème, l’endpoint, l’erreur, et ce que tu as déjà essayé…"
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-amber-300/60 focus:border-amber-300/60 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-100"
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setSupportModalOpen(false)}
                className="h-11 px-4 rounded-2xl text-sm font-semibold border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 transition-colors dark:border-slate-700 dark:bg-slate-900/30 dark:text-slate-200 dark:hover:bg-slate-800/60"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={createSupportTicketFromUI}
                disabled={!String(supportMessage || '').trim() || loading || !sessionId}
                className="h-11 px-5 rounded-2xl text-sm font-semibold bg-amber-500 text-white hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Envoyer au support
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
